"use client";

import { useState } from "react";
import { cn } from "@/core/utils";
import { ChevronDown } from "lucide-react";
import { getAgentTheme } from "../constants/agents";
import { BrunaAvatar } from "@/core/components/bruna/bruna-avatar";
import type { AgentType, Suggestion } from "../types";
import type { AgentHubBlock } from "../schemas/blocks";

/**
 * Renderer do block `agent_hub` (ST-6 — Figma rodapé): accordion de agentes com atalhos.
 * O tema/ícone por agente vem do AGENT_THEMES (por agent_type); o conteúdo (título,
 * descrição, atalhos) vem do block. Clique num atalho reaproveita o fluxo de sugestão.
 */

// Backend usa "strategy"; o registry interno de temas usa "estrategy" (typo do contrato).
function toThemeType(agentType: string): AgentType {
  if (agentType === "strategy" || agentType === "estrategy") return "estrategy";
  if (agentType === "operation") return "operation";
  if (agentType === "experience") return "experience";
  return "operation";
}

export function AgentHub({
  block,
  onSuggestionClick,
}: {
  block: AgentHubBlock;
  onSuggestionClick?: (suggestion: Suggestion) => void;
}) {
  const [openIndex, setOpenIndex] = useState<number>(() =>
    Math.max(
      0,
      block.agents.findIndex((a) => a.expanded),
    ),
  );

  if (!block.agents.length) return null;

  return (
    <div className="flex w-full flex-col gap-2">
      {block.prompt && (
        <span className="text-sm font-medium text-darkgray-700">
          {block.prompt}
        </span>
      )}
      {block.agents.map((agent, index) => {
        const theme = getAgentTheme(toThemeType(agent.agent_type));
        const isOpen = index === openIndex;
        return (
          <div
            key={`${agent.agent_type}-${index}`}
            className="overflow-hidden rounded-xl border border-lightgray-300 bg-white"
          >
            <button
              type="button"
              onClick={() => setOpenIndex(isOpen ? -1 : index)}
              className="flex w-full items-center gap-3 px-3 py-3 text-left"
              aria-expanded={isOpen}
            >
              <BrunaAvatar size={32} />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="text-sm font-semibold text-darkgray-900">
                  {agent.title}
                </span>
                {agent.description && (
                  <span className="truncate text-xs text-darkgray-500">
                    {agent.description}
                  </span>
                )}
              </span>
              <ChevronDown
                className={cn(
                  "h-4 w-4 shrink-0 text-darkgray-400 transition-transform",
                  isOpen && "rotate-180",
                )}
                aria-hidden
              />
            </button>
            {isOpen && agent.actions?.length ? (
              <div className="flex flex-wrap gap-2 px-3 pb-3">
                {agent.actions.map((action) => (
                  <button
                    key={action.action}
                    type="button"
                    onClick={() =>
                      onSuggestionClick?.({
                        text: action.text,
                        action: action.action,
                        category: "agent_hub",
                      })
                    }
                    className="rounded-[10px] border border-lightgray-400 bg-white px-4 py-2 text-sm font-medium text-darkgray-700 transition-colors hover:bg-lightgray-100"
                  >
                    {action.text}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
